import { useNavigate, useRouteError, useRouteLoaderData } from "react-router";
import ErklaeringCard from "../../components/cards/ErklaeringCard";
import { ROUTES, VIRKSOMHEDSBLANKET_PATH } from "../../constants/routes";
import { HomeLoaderResponse } from "../../interfaces/loaders";
import PageError from "../PageError";
import { CUSTOM_MSG } from "../../constants/messages";
import { useQuery } from "@tanstack/react-query";
import { getKlientkontoErklaeringer } from "../../api/klientkontoerklaering";
import { getTilsynsErklaeringer } from "../../api/tilsynserklaering";
import Spinner from "../../components/graphics/Spinner";
import { useEffect } from "react";
import { LinkCard } from "../../components/cards/EntryCard";
import DelegationLawyerSelect from "../../components/delegation/DelegationLawyerSelect";
import { useDelegationContext } from "../../hooks/delegation/useDelegationContext";
import { isAppVisible } from "@/lib/appNavigation";
import { APP_ID } from "@/constants/appNavigation";
import { erIkkePersonligSession } from "@/lib/personligSession";
import FirmaLoginBesked from "./FirmaLoginBesked";

export default function Home() {
  const navigate = useNavigate();
  const { userInfo } = useRouteLoaderData(
    ROUTES.homeIndex.id,
  ) as HomeLoaderResponse;

  const isDelegateOnly = !userInfo?.id;
  const isDelegatedSession = userInfo?.isDelegatedSession ?? false;
  // Samme regel som app-skifteren. Tidligere spurgte kortene kun canAccessAsDelegate, som
  // returnerer true for enhver ikke-delegeret session — ogsaa en firma-session uden person-id.
  // Kortet til efteruddannelse stod derfor tilbage, selv om app-skifteren og ruten var lukket.
  const canAccessSelskabsblanketten = isAppVisible(
    userInfo,
    APP_ID.selskabsblanketten,
  );
  const canAccessEfteruddannelse = isAppVisible(
    userInfo,
    APP_ID.efteruddannelse,
  );
  // ID-kort er personlige: en fuldmægtig kan ikke bestille på advokatens vegne, og en firma- eller
  // revisor-session har ingen person at bestille til (ADV-356). Teksten øverst og genvejen nederst
  // følger begge denne regel.
  const canAccessIdKort =
    !isDelegatedSession && !erIkkePersonligSession(userInfo);
  const delegationContext = useDelegationContext(isDelegateOnly);

  const KKEs = useQuery({
    queryKey: ["KKEs"],
    queryFn: getKlientkontoErklaeringer,
    enabled: !isDelegateOnly && !isDelegatedSession,
  });
  const klientkontoerklaeringer = KKEs.data ?? [];
  const TEs = useQuery({
    queryKey: ["TEs"],
    queryFn: getTilsynsErklaeringer,
    enabled: !isDelegateOnly && !isDelegatedSession,
  });
  const tilsynserklaeringer = TEs.data ?? [];

  useEffect(() => {
    if (userInfo.role === "TokenUser") {
      if (KKEs.data?.length === 1) {
        const kkeId = KKEs.data[0].id;
        navigate(`/klientkontoerklaering/${kkeId}/tilsvar`);
      } else if (TEs.data?.length === 1) {
        const teId = TEs.data[0].id;
        navigate(`/tilsynserklaering/${teId}`);
      }
    }
  }, [navigate, KKEs.data, TEs.data, userInfo.role]);

  if (isDelegateOnly) {
    if (delegationContext.isLoading) {
      return (
        <div className="container-xxl my-7">
          <Spinner size={24} label="Henter fuldmagter..." />
        </div>
      );
    }

    if (delegationContext.isError) {
      return <PageError msg={CUSTOM_MSG.unauth} isLogout />;
    }

    const availableLawyers = delegationContext.data?.availableLawyers ?? [];

    if (availableLawyers.length > 0) {
      return (
        <DelegationLawyerSelect
          delegateName={
            delegationContext.data?.authenticatedMitIdName ??
            userInfo?.mitIdNavn ??
            userInfo?.authenticatedMitIdName
          }
          lawyers={availableLawyers}
        />
      );
    }

    return <PageError msg={CUSTOM_MSG.userNotFound} isLogout />;
  }

  return (
    <div>
      <div className="bg-white pb-8 pt-8 mt-n8">
        <div className="container-xxl">
          <div className="row align-items-center gx-9">
            <div className="col-12 col-lg-10">
              <h1
                className="py-3 display-1 mb-2"
                style={{ fontSize: "2.75rem" }}
              >
                Velkommen {userInfo?.navn}
              </h1>
              <FirmaLoginBesked
                identityType={userInfo?.identityType}
                navn={userInfo?.navn}
              />
              <p className="fw-light">
                Hvis Advokatsamfundet afventer en indberetning fra dig, fremgår
                fristen af oversigten nedenfor. Har du indsendt tilsynserklæring
                eller klientkontoerklæring via denne portal, kan du se, hvornår
                erklæringen er indsendt.
              </p>
              {/* Samme regel som kortet nedenfor: teksten må ikke love en
                  funktion, sessionen ikke får tilbudt. */}
              {canAccessEfteruddannelse ? (
                <p className="fw-light">
                  Under Efteruddannelse, forsikring og garanti finder du
                  oplysning om din indeværende periode og dine indberetninger,
                  og du kan uploade dokumentation for både efteruddannelse,
                  forsikring og garanti.
                </p>
              ) : null}
              <p className="fw-light">
                Under Virksomhedsblanketten kan du udfylde og indsende blanket
                for oprettelse, ændringer eller ophør af en advokatvirksomhed.
              </p>
              {canAccessIdKort ? (
                <p className="fw-light">
                  Under ID-kort finder du en formular til bestilling af nyt
                  ID-kort. Kortet sendes til den adresse, Advokatsamfundet har
                  registreret på dig.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="container-xxl my-7">
        {canAccessSelskabsblanketten ? (
          <>
            <h2 className="h4 mb-4 text-muted">Virksomhedsblanketten</h2>
            <div className="c-card-group w-100 d-flex flex-column gap-4 mb-7">
              <LinkCard
                title="Registrering af virksomhedsforhold"
                description="Udfyld Virksomhedsblanketten for oprettelse, ændring eller ophør af en advokatvirksomhed."
                linkTo={VIRKSOMHEDSBLANKET_PATH}
                linkText="Gå til Virksomhedsblanketten"
              />
            </div>
          </>
        ) : null}

        {canAccessEfteruddannelse ? (
          <>
            <h2 className="h4 mb-4 text-muted">
              Efteruddannelse, forsikring og garanti
            </h2>
            <div className="c-card-group w-100 d-flex flex-column gap-4 mb-7">
              <LinkCard
                title="Efteruddannelse, forsikring og garanti"
                description="Se din indeværende periode, upload dokumentation for efteruddannelse, forsikring og garanti, og se dine indberetninger."
                linkTo="/efteruddannelse"
                linkText="Gå til efteruddannelse"
              />
            </div>
          </>
        ) : null}

        {!isDelegatedSession ? (
          <>
            <h2 className="h4 mb-4 text-muted">Indberetninger</h2>
            <div className="c-card-group w-100 d-flex flex-column gap-4 mb-7">
              {(KKEs.isLoading || TEs.isLoading) && (
                <Spinner size={24} label="Henter erklæringer..." />
              )}
              {
                <>
                  {!KKEs.isLoading &&
                    klientkontoerklaeringer
                      .filter(
                        (e) =>
                          e.status !== "Slettet" &&
                          e.klienterklaeringType === "LoebendeErklaering",
                      )
                      .map((erklaering) => (
                        <div key={erklaering.id}>
                          <ErklaeringCard
                            erklaeringData={erklaering}
                            type="Klientkontoerklæring"
                            href={`klientkontoerklaering/${erklaering.id}`}
                          />
                        </div>
                      ))}
                  {!KKEs.isLoading &&
                    klientkontoerklaeringer
                      .filter(
                        (e) =>
                          e.status !== "Slettet" &&
                          e.klienterklaeringType === "SlutErklaering",
                      )
                      .map((erklaering) => (
                        <div key={erklaering.id}>
                          <ErklaeringCard
                            erklaeringData={erklaering}
                            type="Sluterklæring"
                            href={`sluterklaering/${erklaering.id}`}
                          />
                        </div>
                      ))}
                  {!TEs.isLoading &&
                    tilsynserklaeringer?.map((erklaering) => (
                      <div key={erklaering.id}>
                        <ErklaeringCard
                          erklaeringData={erklaering}
                          type="Tilsynserklæring"
                          href={`tilsynserklaering/${erklaering.id}`}
                        />
                      </div>
                    ))}
                </>
              }
            </div>
          </>
        ) : null}

        {canAccessIdKort ? (
          <>
            <h2 className="h4 mb-4 text-muted">ID-kort</h2>
            <div className="c-card-group w-100 d-flex flex-column gap-4">
              <LinkCard
                title="Bestilling af ID-kort"
                description="Udfyld formularen for at bestille et ID-kort"
                linkTo="/idkort"
                linkText="Gå til bestilling af ID-kort"
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function HomeErrorBoundary() {
  const error = useRouteError();
  console.error(error);
  return <div>Der skete en uventet fejl ..</div>;
}
